// src/components/card/InstallationMenu.tsx

import { TroubleshootTwoTone } from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
  Stack,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { FolderId } from '@/types/domain'
import { Severity } from '@/types/mui'

import {
  getRelativeDate,
  normalizedSensorType,
  SensorIcon,
  SVGStatus,
} from '../../api'
import { HStack } from '../HStack'

type InstallationMenuItemProps = {
  item: any
  onClick: (sensorId: number) => void
}

const InstallationMenuItem = ({ item, onClick }: InstallationMenuItemProps) => {
  const theme = useTheme()
  const success = theme.palette.success.main
  const error = theme.palette.error.main

  const {
    name,
    is_active,
    sensor_type,
    status,
    time,
    type_in_folder: tif,
    value,
    unit,
    entity_id,
  } = item

  const nst = normalizedSensorType(sensor_type, tif)

  const isNotPerformance = !['energy', 'water', 'gas'].includes(nst)

  let src = SensorIcon(nst, status)
  if (!src) src = SensorIcon(tif, [error, success][is_active])

  const secondary = isNotPerformance
    ? `${value} ${unit ? unit : ''} ${getRelativeDate(
        String(new Date(Date.parse(time)))
      )}`
    : `${getRelativeDate(String(new Date(Date.parse(time))))}`

  return (
    <ListItemButton onClick={() => onClick(entity_id)}>
      <ListItemAvatar>
        <Avatar src={src} sx={{ width: 40, height: 40 }} />
      </ListItemAvatar>

      <ListItemText primary={name} secondary={secondary} />
    </ListItemButton>
  )
}

type InstallationMenuProps = {
  id: FolderId
  installationId: number
  name: string
  status: string
  severity: Severity
  sensors: any[]
}

export const InstallationMenu = ({
  id: fId,
  installationId: iId,
  name,
  status,
  severity,
  sensors,
}: InstallationMenuProps) => {
  const navigate = useNavigate()

  const onGotoInstallation = (sensorId?: number) => {
    const sensorQuery = sensorId ? `?sensorId=${sensorId}` : ''
    navigate(`/folder/${fId}/installation/${iId}${sensorQuery}`)
  }

  return (
    <Card variant={'installation'}>
      <CardHeader
        action={
          <Tooltip title={`Details ${name}`}>
            <IconButton onClick={() => onGotoInstallation()}>
              <TroubleshootTwoTone sx={{ mr: 1 }} />{' '}
            </IconButton>
          </Tooltip>
        }
        avatar={
          <Avatar>
            <SVGStatus type={'installation'} status={status} />
          </Avatar>
        }
        title={name}
        variant={'installation_overview'}
      />
      <CardContent sx={{ p: '0px !important' }}>
        {severity !== 'success' && (
          <Alert severity={severity}>
            {severity === 'warning' && (
              <>zuletzt aktualisiert vor mehr als 24h</>
            )}
            {severity === 'error' && <>zuletzt aktualisiert vor mehr als 72h</>}
          </Alert>
        )}

        <HStack sx={{ alignItems: 'top' }}>
          <Stack>
            <Typography align={'center'} sx={{ mt: 1 }}>
              Messpunkte
            </Typography>

            <List>
              {sensors.map(item => (
                <InstallationMenuItem
                  item={item}
                  key={item.entity_id}
                  onClick={onGotoInstallation}
                />
              ))}
            </List>
          </Stack>

          <Stack>
            <Typography align={'center'} sx={{ mt: 1 }}>
              Kennzahlen
            </Typography>
          </Stack>
        </HStack>
      </CardContent>
    </Card>
  )
}
